/*
 File: App.tsx
 Path: src/App.tsx
 Description: Main entry for AQUORIX Pro/Admin Dashboard.
 Version: 1.5.3
 Last Updated: 2026-07-28

 Phase C Lock:
 - Tier 0 Admin lands on /dashboard (shared shell)
 - /admin/* routes removed to prevent legacy gravity + churn
 - / -> /login
 - /signin -> /login
 - AuthCallback only on /auth/callback

 Change Log:
 - 2026-02-07 - v1.5.1 - DIR Fix: Add /admin/* routes
 - 2026-07-28 - v1.5.2 - Add the Manifest dashboard route.
 - 2026-08-18 - v1.5.3 - Update details for Agencies, Forms and Vitals (was Metrics)
*/

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Login from './pages/Login';
import Signup from './pages/Signup';

import Onboarding from './pages/Onboarding';
import SetPassword from './pages/SetPassword';

import RequireAuth from './components/RequireAuth';
import { SystemHealthLogProvider } from './context/SystemHealthLogContext';

import ProDashboardShell from './features/dashboard/ProDashboardShell';
import OpsBoardPage from './features/dashboard/pages/OpsBoardPage';
import SchedulePage from './features/dashboard/pages/SchedulePage';
import DiveSitesPage from './features/dashboard/pages/DiveSitesPage';
import ManifestPage from './features/dashboard/pages/ManifestPage';
import VehiclesVesselsPage from './features/dashboard/pages/VehiclesVesselsPage';
import CalendarPage from './features/dashboard/pages/CalendarPage';
import BookingsPage from './features/dashboard/pages/BookingsPage';
import BookingDetailPage from './features/dashboard/pages/BookingDetailPage';
import CoursesPage from './features/dashboard/pages/CoursesPage';
import TeamPage from './features/dashboard/pages/TeamPage';
import ContentMediaPage from './features/dashboard/pages/ContentMediaPage';
import ProfilePage from './features/dashboard/pages/ProfilePage';
import SettingsPage from './features/dashboard/pages/SettingsPage';
import AgenciesPage from './features/dashboard/pages/AgenciesPage';
import FormsPage from './features/dashboard/pages/FormsPage';
import VitalsPage from './features/dashboard/pages/VitalsPage';
import WalkInManager from './features/dashboard/pages/WalkInManager';
import CustomersPage from './features/dashboard/pages/CustomersPage';
import CustomerDetailPage from './features/dashboard/pages/CustomerDetailPage';
import BillingReportPage from './features/dashboard/pages/BillingReportPage';
import OpsBoardApp from './features/ops-board/OpsBoardApp';
import OpsBoardV2 from './features/ops-board-v2/OpsBoardV2';

import AuthCallback from './pages/AuthCallback';

import AQXAdminLayout from './layouts/AQXAdminLayout';
import AdminOverview from './pages/admin/AdminOverview';
import BillingHomePage from './features/dashboard/pages/BillingHomePage';
import CustomerConfirmPage from './features/customer-acknowledgment/CustomerConfirmPage';

function App() {
  return (
    <SystemHealthLogProvider>
      <Routes>
        {/* Default entry */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/signin" element={<Navigate to="/login" replace />} />

        {/* Supabase auth callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Auth pages */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/set-password" element={<SetPassword />} />

        {/* Onboarding */}
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Public customer acknowledgment route — Viking Completion Loop Phase 3. */}
        <Route path="/confirm/:token" element={<CustomerConfirmPage />} />

        {/* Admin (Tier 0) */}
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AQXAdminLayout />
            </RequireAuth>
          }
        >
          {/* Default /admin -> /admin/overview */}
          <Route index element={<Navigate to="/admin/overview" replace />} />
          <Route path="overview" element={<AdminOverview />} />
        </Route>

        {/* Public Ops Board kiosk route — P10.18 read-only operator-slug mode for Pi / kiosk displays. */}
        <Route path="/ops-board/public/:operatorSlug" element={<OpsBoardV2 />} />

        {/* Authenticated Ops Board dashboard route. */}
        <Route
          path="/ops-board"
          element={
            <RequireAuth>
              <OpsBoardApp />
            </RequireAuth>
          }
        />

        {/* Dashboard (ALL authenticated users: admin + pro + affiliate) */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <ProDashboardShell />
            </RequireAuth>
          }
        >
          <Route index element={<OpsBoardPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="manifest" element={<ManifestPage />} />
          <Route path="sites" element={<DiveSitesPage />} />
          <Route path="vehicles-vessels" element={<VehiclesVesselsPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="walk-ins" element={<WalkInManager />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:guest_id" element={<CustomerDetailPage />} />
          <Route path="billing" element={<BillingHomePage />} />
          <Route path="billing/:booking_id" element={<BillingReportPage />} />
          <Route path="bookings/:bookingId" element={<BookingDetailPage />} />
          <Route path="courses" element={<CoursesPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="content-media" element={<ContentMediaPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="agencies" element={<AgenciesPage />} />
          <Route path="forms" element={<FormsPage />} />
          <Route path="vitals" element={<VitalsPage />} />
        </Route>

        {/* Not Authorized */}
        <Route path="/not-authorized" element={React.createElement(require('./pages/NotAuthorized').default)} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </SystemHealthLogProvider>
  );
}

export default App;
